import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import { Camera, CheckCircle2, FileSignature, FileText, Loader2, PenLine, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/company-property`;

// Downscale a camera photo in the browser so uploads stay small (max 1400px, JPEG 0.85).
const shrink = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1400 / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    resolve(c.toDataURL('image/jpeg', 0.85));
  };
  img.onerror = reject;
  img.src = url;
});

function ItemCard({ item, agreementId, photoId, confirmed, onConfirm, onPhoto }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const token = localStorage.getItem('token');
  const take = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const dataUrl = await shrink(f);
      const r = await axios.post(`${API}/agreements/${agreementId}/photo`, { item_id: item.id, image: dataUrl });
      setPreview(dataUrl);
      onPhoto(item.id, r.data.photo_id);
      toast.success('Photo saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Photo upload failed'); }
    setBusy(false);
    e.target.value = '';
  };
  const hasPhoto = !!photoId || !!preview;
  return (
    <div className={`rounded-xl border p-4 ${confirmed && (!item.photo_required || hasPhoto) ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`} data-testid={`paperwork-item-${item.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-slate-900 font-semibold">{item.type_label}: {item.description}</p>
          {item.identifier && <p className="text-xs text-slate-500">Serial / ID on record: {item.identifier}</p>}
        </div>
        {confirmed && (!item.photo_required || hasPhoto) && <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0" />}
      </div>
      {item.photo_required && (
        <div className="mt-3">
          <p className="text-xs text-amber-700 mb-2">📷 {item.photo_hint}</p>
          <div className="flex items-center gap-3">
            <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer ${hasPhoto ? 'bg-slate-100 text-slate-800' : 'bg-amber-500 text-slate-900 hover:bg-amber-400'}`} data-testid={`paperwork-photo-btn-${item.id}`}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : hasPhoto ? <RefreshCw className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
              {hasPhoto ? 'Retake photo' : 'Take photo'}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={take} disabled={busy} data-testid={`paperwork-photo-input-${item.id}`} />
            </label>
            {hasPhoto && (
              <img src={preview || `${API}/photos/${photoId}?token=${encodeURIComponent(token)}`} alt="serial" className="h-14 w-20 object-cover rounded border border-slate-300" data-testid={`paperwork-photo-preview-${item.id}`} />
            )}
          </div>
        </div>
      )}
      <label className="mt-3 flex items-start gap-3 text-sm text-slate-800 cursor-pointer">
        <input type="checkbox" checked={!!confirmed} onChange={e => onConfirm(item.id, e.target.checked)} className="mt-0.5 w-5 h-5 rounded shrink-0" data-testid={`paperwork-confirm-${item.id}`} />
        <span>I received this item and it is in good working order.</span>
      </label>
    </div>
  );
}

function AgreementForm({ ag, photos: initialPhotos, onSigned }) {
  const [confirm, setConfirm] = useState({});
  const [photos, setPhotos] = useState(initialPhotos || {});
  const [hasInk, setHasInk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sigRef = useRef(null);
  const ready = ag.items.every(i => confirm[i.id] && (!i.photo_required || photos[i.id])) && hasInk;
  const sign = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/agreements/${ag.id}/sign`, { signature_data: sigRef.current.toDataURL('image/png'), confirmations: confirm });
      toast.success('Signed — thank you!');
      onSigned(ag.id, r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record your signature'); }
    setSubmitting(false);
  };
  return (
    <div className="bg-white border-2 border-amber-500 rounded-2xl shadow-2xl overflow-hidden" data-testid={`paperwork-agreement-${ag.id}`}>
      <div className="p-5 border-b border-slate-200">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-wide bg-amber-500 text-slate-900"><FileSignature className="w-4 h-4" /> COMPANY PROPERTY — SIGNATURE REQUIRED</span>
        <h2 className="mt-3 text-xl font-bold text-slate-900">{ag.items.length} item{ag.items.length !== 1 ? 's' : ''} issued to you by {ag.issued_by}</h2>
        <p className="text-xs text-slate-500 mt-1">Issued {new Date(ag.issued_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}{ag.note ? ` · "${ag.note}"` : ''}</p>
      </div>
      <div className="p-5 space-y-3">
        {ag.items.map(it => <ItemCard key={it.id} item={it} agreementId={ag.id} photoId={photos[it.id]} confirmed={confirm[it.id]}
          onConfirm={(id, v) => setConfirm(c => ({ ...c, [id]: v }))} onPhoto={(id, pid) => setPhotos(p => ({ ...p, [id]: pid }))} />)}
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">Agreement</p>
          <p className="text-sm text-slate-800 leading-relaxed" data-testid="paperwork-agreement-text">{ag.agreement_text}</p>
        </div>
      </div>
      <div className="p-5 border-t border-slate-200">
        <div className="flex items-center gap-2 mb-2"><PenLine className="w-4 h-4 text-slate-500" /><p className="text-sm font-semibold text-slate-900">Sign below with your finger</p></div>
        <div className="bg-white rounded-xl overflow-hidden border border-slate-300" data-testid="paperwork-sign-canvas">
          <SignatureCanvas ref={sigRef} penColor="black" backgroundColor="white" onEnd={() => setHasInk(true)}
            canvasProps={{ className: 'w-full', style: { width: '100%', height: '180px' } }} />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <button onClick={() => { sigRef.current?.clear(); setHasInk(false); }} className="px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">Clear</button>
          <button onClick={sign} disabled={!ready || submitting} data-testid="paperwork-sign-btn"
            className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? 'Saving…' : 'Sign & confirm receipt'}
          </button>
        </div>
        {!ready && <p className="mt-2 text-center text-xs text-amber-700" data-testid="paperwork-ready-hint">Tick every item{ag.items.some(i => i.photo_required) ? ', take the required photos' : ''} and sign to continue.</p>}
      </div>
    </div>
  );
}

function RetakeCard({ retake, onDone }) {
  const [busy, setBusy] = useState(null);
  const [previews, setPreviews] = useState({});
  const [hasInk, setHasInk] = useState(false);
  const sigRef = useRef(null);
  const [pendingUploads, setPendingUploads] = useState({});
  const needsSig = retake.needs_signature;
  const upload = async (item, dataUrl) => {
    const body = { item_id: item.id, image: dataUrl };
    if (needsSig && sigRef.current && !sigRef.current.isEmpty()) body.signature_data = sigRef.current.toDataURL('image/png');
    const r = await axios.post(`${API}/agreements/${retake.agreement_id}/photo`, body);
    return r.data;
  };
  const take = async (item, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(item.id);
    try {
      const dataUrl = await shrink(f);
      setPreviews(p => ({ ...p, [item.id]: dataUrl }));
      if (needsSig && (!sigRef.current || sigRef.current.isEmpty())) {
        setPendingUploads(p => ({ ...p, [item.id]: dataUrl }));
        toast.info('Photo ready — sign below to submit it');
      } else {
        const r = await upload(item, dataUrl);
        toast.success(r.retake_complete ? 'New photo submitted — thanks, your agreement has been updated' : 'Photo saved');
        if (r.retake_complete) onDone();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setBusy(null); e.target.value = '';
  };
  const submitWithSig = async () => {
    const entries = Object.entries(pendingUploads);
    if (!entries.length || !sigRef.current || sigRef.current.isEmpty()) return;
    setBusy('sig');
    try {
      let done = false;
      for (const [itemId, dataUrl] of entries) {
        const item = retake.items.find(i => i.id === itemId);
        const r = await upload(item, dataUrl);
        done = done || r.retake_complete;
      }
      toast.success('Submitted — your agreement has been updated');
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setBusy(null);
  };
  return (
    <div className="bg-white border-2 border-red-500 rounded-2xl shadow-2xl overflow-hidden" data-testid={`paperwork-retake-${retake.agreement_id}`}>
      <div className="p-5 border-b border-slate-200">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-wide bg-red-500 text-white"><Camera className="w-4 h-4" /> PHOTO RETAKE REQUESTED</span>
        <h2 className="mt-3 text-xl font-bold text-slate-900">HR needs a clearer photo</h2>
        <p className="text-xs text-slate-500 mt-1">Your agreement stays signed — just replace the photo{retake.items.length > 1 ? 's' : ''} below.</p>
      </div>
      <div className="p-5 space-y-3">
        {retake.items.map(item => (
          <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`paperwork-retake-item-${item.id}`}>
            <p className="text-slate-900 font-semibold">{item.type_label}: {item.description}</p>
            <p className="text-sm text-red-700 mt-1" data-testid={`paperwork-retake-reason-${item.id}`}>"{item.photo_reject_reason}" — {item.photo_rejected_by}</p>
            <p className="text-xs text-amber-700 mt-2">📷 {item.photo_hint}</p>
            <div className="flex items-center gap-3 mt-2">
              <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold cursor-pointer bg-amber-500 text-slate-900 hover:bg-amber-400" data-testid={`paperwork-retake-btn-${item.id}`}>
                {busy === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {previews[item.id] ? 'Retake again' : 'Take new photo'}
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => take(item, e)} disabled={!!busy} data-testid={`paperwork-retake-input-${item.id}`} />
              </label>
              {previews[item.id] && <img src={previews[item.id]} alt="new" className="h-14 w-20 object-cover rounded border border-slate-300" />}
            </div>
          </div>
        ))}
        {needsSig && (
          <div>
            <p className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><PenLine className="w-4 h-4 text-slate-500" /> Sign to confirm the new photo is of your item</p>
            <div className="bg-white rounded-xl overflow-hidden border border-slate-300">
              <SignatureCanvas ref={sigRef} penColor="black" backgroundColor="white" onEnd={() => setHasInk(true)} canvasProps={{ className: 'w-full', style: { width: '100%', height: '160px' } }} />
            </div>
            <button onClick={submitWithSig} disabled={!hasInk || !Object.keys(pendingUploads).length || busy === 'sig'} data-testid={`paperwork-retake-submit-${retake.agreement_id}`}
              className="mt-3 w-full px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold disabled:opacity-40">Submit new photo{Object.keys(pendingUploads).length > 1 ? 's' : ''}</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MyPaperwork() {
  const [data, setData] = useState(null);
  const [justSigned, setJustSigned] = useState({});
  const token = localStorage.getItem('token');
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/my`); setData(r.data); } catch { setData({ pending: [], agreements: [], items: [], photos: {} }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-6 text-blue-800">Loading…</div>;
  const signed = data.agreements.filter(a => a.status === 'signed');
  const held = data.items.filter(i => i.status === 'issued');
  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6" data-testid="my-paperwork-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3"><FileSignature className="w-7 h-7" /> My Paperwork</h1>
        <p className="text-blue-800 text-sm">Company property receipts you need to sign, and what's currently signed out to you</p>
      </div>
      {(data.retakes || []).map(rt => <RetakeCard key={rt.agreement_id} retake={rt} onDone={load} />)}
      {data.pending.length === 0 && (data.retakes || []).length === 0 ? (
        <div className="bg-slate-900/5 rounded-xl p-6 text-center text-blue-800" data-testid="paperwork-none">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-700" /> Nothing to sign right now.
          {Object.keys(justSigned).length > 0 && <p className="text-xs mt-2">Your signed copy has been filed to your employee folder.</p>}
        </div>
      ) : data.pending.length === 0 ? null : data.pending.map(ag => <AgreementForm key={ag.id} ag={ag} photos={data.photos} onSigned={(id, r) => { setJustSigned(j => ({ ...j, [id]: r })); load(); }} />)}

      {held.length > 0 && (
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm font-semibold text-slate-800 mb-2">Signed out to you ({held.length})</p>
          <ul className="divide-y divide-slate-100">
            {held.map(i => (
              <li key={i.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`paperwork-held-${i.id}`}>
                <span className="text-slate-800">{i.type_label}: {i.description}{i.identifier ? <span className="text-slate-500"> · {i.identifier}</span> : ''}</span>
                <span className="text-xs text-slate-500 whitespace-nowrap">since {new Date(i.issued_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {signed.length > 0 && (
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm font-semibold text-slate-800 mb-2">Signed agreements</p>
          <ul className="space-y-1">
            {signed.map(a => (
              <li key={a.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-700">{a.items.length} item{a.items.length !== 1 ? 's' : ''} · signed {a.signed_at_et || new Date(a.signed_at).toLocaleDateString()}</span>
                <a href={`${API}/agreements/${a.id}/pdf?token=${encodeURIComponent(token)}`} target="_blank" rel="noreferrer" data-testid={`paperwork-pdf-${a.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded text-slate-700"><FileText className="w-3.5 h-3.5" /> PDF</a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
